import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { MarkdownEditor } from './ui/MarkdownEditor';
import {
  BlogPost, UserProfile, FactoryDrop, ProductionItem,
  ContentPillar, ContentCluster, PILLAR_META, CLUSTER_META,
} from '../types';
import { 
  getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost,
  getFactoryDrops, createFactoryDrop, deleteFactoryDrop,
  getProductionSchedule, createProductionItem, deleteProductionItem,
  mockSignInWithGoogle, mockSignOut, subscribeToAuthChanges 
} from '../services/firebase';
import { Edit2, Trash2, Plus, LogOut, Loader, Save, X, Globe, Lock, Box, FileCode, FileJson, Link as LinkIcon } from 'lucide-react';
import { SEO } from './SEO';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const calculateReadTime = (text: string): string => {
  if (!text) return '1 min read';
  const words = text.trim().split(/\s+/).length;
  return `${Math.ceil(words / 200)} min read`;
};

const getCategoryDefaultImage = (category?: string) => {
  const n = category?.toLowerCase() || '';
  if (n.includes('technical')) return '/og/engineering.png';
  if (n.includes('gateway'))   return '/og/public.png';
  if (n.includes('product'))   return '/og/product.png';
  if (n.includes('founder'))   return '/og/philosophy.png';
  return '/og/default.png';
};

// The 4 pillars, sorted by their declared order.
const PILLAR_OPTIONS = (Object.keys(PILLAR_META) as ContentPillar[])
  .sort((a, b) => PILLAR_META[a].order - PILLAR_META[b].order);

// Clusters grouped under their parent pillar, each sorted by declared order.
// Used to filter the cluster dropdown to only the selected pillar's clusters.
const clustersForPillar = (pillar?: ContentPillar): ContentCluster[] => {
  if (!pillar) return [];
  return (Object.keys(CLUSTER_META) as ContentCluster[])
    .filter(c => CLUSTER_META[c].pillar === pillar)
    .sort((a, b) => CLUSTER_META[a].order - CLUSTER_META[b].order);
};

// Audiences that map to canonicalQuestions keys
const CANONICAL_AUDIENCES = ['developer', 'founder', 'investor', 'merchant', 'consumer'];

// ─── Component ────────────────────────────────────────────────────────────────

export const AdminDashboard: React.FC = () => {
  const [user, setUser]           = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState<'posts' | 'drops' | 'schedule'>('posts');
  const [loading, setLoading]     = useState(true);

  // Blog state
  const [posts, setPosts]           = useState<BlogPost[]>([]);
  const [editingPost, setEditingPost] = useState<Partial<BlogPost> | null>(null);
  const [coverFile, setCoverFile]   = useState<File | null>(null);

  // Drops state
  const [drops, setDrops]   = useState<FactoryDrop[]>([]);
  const [newDrop, setNewDrop] = useState<Partial<FactoryDrop> | null>(null);

  // Schedule state
  const [schedule, setSchedule] = useState<ProductionItem[]>([]);
  const [newItem, setNewItem]   = useState<Partial<ProductionItem> | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(u => {
      setUser(u);
      if (u?.isAdmin) fetchAll();
      else setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [p, d, s] = await Promise.all([getBlogPosts(), getFactoryDrops(), getProductionSchedule()]);
    setPosts(p); setDrops(d); setSchedule(s);
    setLoading(false);
  };

  // ── Blog handlers ────────────────────────────────────────────────────────────

  const handleSavePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPost) return;
    setIsSaving(true);

    const postToSave = {
      ...editingPost,
      readTime: editingPost.readTime || calculateReadTime(editingPost.content || ''),
      // playlist now shares the same ContentPillar taxonomy as contentPillar —
      // defaults to whatever pillar was selected so video grouping and the
      // article's own pillar stay in sync unless explicitly overridden.
      playlist: editingPost.playlist || editingPost.contentPillar,
    };

    if (postToSave.id) await updateBlogPost(postToSave.id, postToSave, coverFile || undefined);
    else               await createBlogPost(postToSave, coverFile || undefined);

    await fetchAll();
    setEditingPost(null);
    setCoverFile(null);
    setIsSaving(false);
  };

  const handleDeletePost = async (id: string) => {
    if (confirm('Delete this post?')) { await deleteBlogPost(id); fetchAll(); }
  };

  // Handles pillar selection — clears contentCluster if it no longer belongs
  // to the newly selected pillar, so the two fields can never mismatch.
  const handlePillarChange = (pillar: ContentPillar) => {
    setEditingPost(prev => {
      const next = { ...prev, contentPillar: pillar };
      if (prev?.contentCluster && CLUSTER_META[prev.contentCluster].pillar !== pillar) {
        next.contentCluster = undefined;
      }
      return next;
    });
  };

  // ── Drop handlers ────────────────────────────────────────────────────────────

  const handleSaveDrop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDrop) return;
    setIsSaving(true);
    await createFactoryDrop(newDrop);
    await fetchAll();
    setNewDrop(null);
    setIsSaving(false);
  };

  const handleDeleteDrop = async (id: string) => {
    if (confirm('Delete this drop?')) { await deleteFactoryDrop(id); fetchAll(); }
  };

  // ── Schedule handlers ────────────────────────────────────────────────────────

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem) return;
    setIsSaving(true);
    await createProductionItem({ ...newItem, votes: 0 });
    await fetchAll();
    setNewItem(null);
    setIsSaving(false);
  };

  const handleDeleteItem = async (id: string) => {
    if (confirm('Delete this item?')) { await deleteProductionItem(id); fetchAll(); }
  };

  // ── canonicalQuestions helpers ───────────────────────────────────────────────

  const setCQ = (audience: string, value: string) => {
    const updated = { ...(editingPost?.canonicalQuestions ?? {}), [audience]: value };
    if (!value) delete updated[audience];
    setEditingPost(prev => ({ ...prev!, canonicalQuestions: updated }));
  };

  // ─── Guards ───────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex justify-center items-center h-screen">
      <Loader className="animate-spin text-brand-blue" />
    </div>
  );

  if (!user || !user.isAdmin) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <h1 className="text-2xl font-bold mb-6">Factory Control Panel</h1>
      <p className="mb-6 text-slate-500">Restricted Access. Personnel Only.</p>
      <Button onClick={mockSignInWithGoogle}>Sign In with Google</Button>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────────

  const availableClusters = clustersForPillar(editingPost?.contentPillar);

  return (
    <>
      <SEO title="Dashboard" description="Five Dollar Workday Dashboard" noindex={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-display font-bold">Factory Control</h1>
          <div className="flex gap-4 items-center">
            <span className="text-sm font-medium text-slate-600">{user.displayName}</span>
            <Button variant="outline" size="sm" onClick={mockSignOut}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 border-b border-slate-200 mb-8">
          {(['posts', 'drops', 'schedule'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-b-2 border-brand-blue text-brand-blue'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── POSTS TAB ──────────────────────────────────────────────────────── */}
        {activeTab === 'posts' && (
          <>
            {editingPost ? (
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 max-w-5xl mx-auto">

                {/* Form header */}
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">
                    {editingPost.id ? 'Edit Log Entry' : 'New Log Entry'}
                  </h2>
                  <button
                    onClick={() => { setEditingPost(null); setCoverFile(null); }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSavePost} className="space-y-6">

                  {/* ── Strategy & Distribution ─────────────────────────────── */}
                  <div className="grid grid-cols-2 gap-6 p-6 bg-brand-slate rounded-xl border border-slate-200">
                    <div className="col-span-2 flex justify-between items-center border-b border-slate-200 pb-4 mb-2">
                      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-600" />
                        Strategy & Distribution
                      </h3>
                      <span className="text-xs font-mono text-slate-500 uppercase tracking-wider">
                        Infrastructure Layer
                      </span>
                    </div>

                    {/* YouTube ID */}
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                        YouTube Video ID
                      </label>
                      <div className="flex gap-2">
                        <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-slate-300 bg-slate-100 text-slate-500 text-sm">
                          v=
                        </span>
                        <input
                          type="text"
                          placeholder="dQw4w9WgXcQ"
                          value={editingPost.youtubeId || ''}
                          onChange={e => setEditingPost({ ...editingPost, youtubeId: e.target.value })}
                          className="block w-full min-w-0 flex-1 rounded-none rounded-r-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-black focus:ring-brand-black"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Populates VideoSchema automatically.</p>
                    </div>

                    {/* Narrative Series (category — free text, kept for backward compatibility) */}
                    <div>
                      <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                        Narrative Series (legacy category)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Build Log"
                        value={editingPost.category || ''}
                        onChange={e => setEditingPost({ ...editingPost, category: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-black focus:ring-brand-black"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">
                        Legacy free-text field. New posts should rely on Content Pillar below instead.
                      </p>
                    </div>

                    {/* Content Pillar */}
                    <div className="col-span-2">
                      <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                        Content Pillar
                      </label>
                      <div className="flex flex-wrap gap-4">
                        {PILLAR_OPTIONS.map(p => (
                          <label key={p} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="contentPillar"
                              checked={editingPost.contentPillar === p}
                              onChange={() => handlePillarChange(p)}
                              className="text-brand-black focus:ring-brand-black"
                            />
                            <span className="text-sm text-slate-700">{PILLAR_META[p].label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Determines which pillar page this post links up to (or, for the pillar page itself, which pillar it represents).
                      </p>
                    </div>

                    {/* Content Cluster — filtered to the selected pillar */}
                    <div className="col-span-2">
                      <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                        Content Cluster
                      </label>
                      <select
                        value={editingPost.contentCluster || ''}
                        onChange={e => setEditingPost({
                          ...editingPost,
                          contentCluster: (e.target.value || undefined) as ContentCluster | undefined,
                        })}
                        disabled={!editingPost.contentPillar}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-black focus:ring-brand-black disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        <option value="">
                          {editingPost.contentPillar ? '— No cluster (this is the pillar page) —' : 'Select a Content Pillar first'}
                        </option>
                        {availableClusters.map(c => (
                          <option key={c} value={c}>{CLUSTER_META[c].label}</option>
                        ))}
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Leave blank if this post IS the pillar page. Otherwise, choose the section this article belongs under.
                      </p>
                    </div>
                  </div>

                  {/* ── Title + Public toggle ────────────────────────────────── */}
                  <div className="flex gap-6 items-start">
                    <div className="flex-grow">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={editingPost.title || ''}
                        onChange={e => setEditingPost({ ...editingPost, title: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-4 py-2"
                        required
                      />
                    </div>
                    <div className="pt-7">
                      <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-brand-slate">
                        <input
                          type="checkbox"
                          checked={editingPost.isPublic || false}
                          onChange={e => setEditingPost({ ...editingPost, isPublic: e.target.checked })}
                          className="w-4 h-4 text-brand-blue rounded"
                        />
                        <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                          {editingPost.isPublic
                            ? <Globe className="w-4 h-4 text-green-600" />
                            : <Lock className="w-4 h-4 text-slate-400" />}
                          Public
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* ── Cover Image ──────────────────────────────────────────── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Cover Image</label>
                    <div className="flex items-start gap-6 p-4 bg-brand-slate rounded-lg border border-slate-200">
                      <div className="w-40 h-24 flex-shrink-0 bg-white rounded-md border border-slate-200 overflow-hidden flex items-center justify-center relative">
                        {coverFile ? (
                          <img src={URL.createObjectURL(coverFile)} alt="Preview" className="w-full h-full object-cover" />
                        ) : editingPost.coverImageUrl ? (
                          <img src={editingPost.coverImageUrl} alt="Current" className="w-full h-full object-cover" />
                        ) : (
                          <>
                            <img
                              src={getCategoryDefaultImage(editingPost.category)}
                              alt="Default"
                              className="w-full h-full object-cover opacity-50 grayscale"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="bg-slate-900/70 text-white text-[10px] uppercase font-bold px-2 py-1 rounded">
                                Auto-Default
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex-grow">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => setCoverFile(e.target.files?.[0] || null)}
                          className="block w-full text-sm text-slate-500
                            file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0
                            file:text-sm file:font-semibold file:bg-brand-blue file:text-white
                            hover:file:bg-blue-700 cursor-pointer"
                        />
                        <p className="mt-2 text-xs text-slate-500">
                          {editingPost.coverImageUrl
                            ? 'Upload a new file to replace the current image.'
                            : 'Leave empty to use the Automatic Category Default (shown left).'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── Canonical URL ────────────────────────────────────────── */}
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-1">
                      <LinkIcon className="w-3 h-3" /> Canonical URL (Optional)
                    </label>
                    <input
                      type="text"
                      value={editingPost.canonicalUrl || ''}
                      onChange={e => setEditingPost({ ...editingPost, canonicalUrl: e.target.value })}
                      placeholder="e.g. https://medium.com/@user/post"
                      className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-mono text-slate-600"
                    />
                  </div>

                  {/* ── Excerpt ──────────────────────────────────────────────── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Excerpt</label>
                    <textarea
                      value={editingPost.excerpt || ''}
                      onChange={e => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 px-4 py-2 h-20"
                      required
                    />
                  </div>

                  {/* ── Content ──────────────────────────────────────────────── */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                    <MarkdownEditor
                      value={editingPost.content || ''}
                      onChange={val => setEditingPost({
                        ...editingPost,
                        content: val,
                        readTime: calculateReadTime(val),
                      })}
                    />
                  </div>

                  {/* ── Canonical Questions (FAQ / E-E-A-T) ─────────────────── */}
                  <div className="p-6 bg-brand-slate rounded-xl border border-slate-200 space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-200 pb-4">
                      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-600" />
                        Canonical Questions
                      </h3>
                      <span className="text-xs font-mono text-slate-500 uppercase tracking-wider">
                        FAQ Schema / E-E-A-T
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      One question per audience. These inject <code className="bg-slate-100 px-1 rounded">FAQPage</code> JSON-LD
                      at static build time and in the runtime blog view. Pulled automatically
                      from the Buildlog source entry if left blank here.
                    </p>
                    {CANONICAL_AUDIENCES.map(audience => (
                      <div key={audience} className="flex gap-3 items-center">
                        <label className="w-24 shrink-0 text-xs font-mono uppercase tracking-wider text-slate-500">
                          {audience}
                        </label>
                        <input
                          type="text"
                          placeholder={`Question for ${audience} audience…`}
                          value={editingPost.canonicalQuestions?.[audience] ?? ''}
                          onChange={e => setCQ(audience, e.target.value)}
                          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm
                                     focus:border-brand-black focus:ring-brand-black font-light"
                        />
                      </div>
                    ))}
                  </div>

                  {/* ── Save / Cancel ─────────────────────────────────────────── */}
                  <div className="flex justify-end pt-4">
                    <Button
                      type="button" variant="ghost"
                      onClick={() => { setEditingPost(null); setCoverFile(null); }}
                      className="mr-4"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving}>
                      {isSaving
                        ? <Loader className="w-4 h-4 animate-spin mr-2" />
                        : <Save className="w-4 h-4 mr-2" />}
                      Save Entry
                    </Button>
                  </div>

                </form>
              </div>

            ) : (
              /* ── Posts list ────────────────────────────────────────────────── */
              <div>
                <div className="flex justify-end mb-6">
                  <Button onClick={() => setEditingPost({
                    isPublic: false,
                    readTime: '1 min read',
                  })}>
                    <Plus className="w-4 h-4 mr-2" /> New Entry
                  </Button>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-brand-slate">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Pillar</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Cluster</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">FAQ</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {posts.map(post => (
                        <tr key={post.id} className="hover:bg-brand-slate">
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {post.isPublic
                              ? <Globe className="w-4 h-4 text-green-500" />
                              : <Lock className="w-4 h-4 text-slate-400" />}
                          </td>
                          <td className="px-6 py-4 font-medium text-slate-900">{post.title}</td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {post.contentPillar ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                                {PILLAR_META[post.contentPillar].label}
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs font-mono">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {post.contentCluster ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                {CLUSTER_META[post.contentCluster].label}
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs font-mono">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {post.canonicalQuestions && Object.keys(post.canonicalQuestions).length > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                {Object.keys(post.canonicalQuestions).length}Q
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs font-mono">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button onClick={() => setEditingPost(post)} className="text-brand-blue hover:text-blue-900 mr-4">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDeletePost(post.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── DROPS TAB ──────────────────────────────────────────────────────── */}
        {activeTab === 'drops' && (
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-1">
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="font-bold mb-4">Create New Drop</h3>
                <form onSubmit={handleSaveDrop} className="space-y-4">
                  <input type="text" placeholder="Title" className="w-full border rounded p-2"
                    value={newDrop?.title || ''} onChange={e => setNewDrop({ ...newDrop, title: e.target.value })} required />
                  <textarea placeholder="Description" className="w-full border rounded p-2"
                    value={newDrop?.desc || ''} onChange={e => setNewDrop({ ...newDrop, desc: e.target.value })} required />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="Type (e.g. Config)" className="w-full border rounded p-2"
                      value={newDrop?.type || ''} onChange={e => setNewDrop({ ...newDrop, type: e.target.value })} required />
                    <select className="w-full border rounded p-2"
                      value={newDrop?.iconType || 'box'}
                      onChange={e => setNewDrop({ ...newDrop, iconType: e.target.value as any })}>
                      <option value="box">Box</option>
                      <option value="code">Code</option>
                      <option value="json">JSON</option>
                    </select>
                  </div>
                  <Button type="submit" disabled={isSaving} className="w-full">Create Drop</Button>
                </form>
              </div>
            </div>
            <div className="md:col-span-2 space-y-4">
              {drops.map(drop => (
                <div key={drop.id} className="flex justify-between items-center p-4 bg-white border rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-slate-100 rounded">
                      {drop.iconType === 'json' ? <FileJson className="w-5 h-5" />
                        : drop.iconType === 'code' ? <FileCode className="w-5 h-5" />
                        : <Box className="w-5 h-5" />}
                    </div>
                    <div>
                      <div className="font-bold">{drop.title}</div>
                      <div className="text-xs text-slate-500">{drop.desc}</div>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteDrop(drop.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SCHEDULE TAB ───────────────────────────────────────────────────── */}
        {activeTab === 'schedule' && (
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-1">
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="font-bold mb-4">Add Schedule Item</h3>
                <form onSubmit={handleSaveItem} className="space-y-4">
                  <input type="text" placeholder="Feature Name" className="w-full border rounded p-2"
                    value={newItem?.name || ''} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required />
                  <select className="w-full border rounded p-2"
                    value={newItem?.status || 'Concept'}
                    onChange={e => setNewItem({ ...newItem, status: e.target.value as any })}>
                    <option>Concept</option><option>In Dev</option><option>Beta</option><option>Shipped</option>
                  </select>
                  <Button type="submit" disabled={isSaving} className="w-full">Add To Schedule</Button>
                </form>
              </div>
            </div>
            <div className="md:col-span-2 space-y-4">
              {schedule.map(item => (
                <div key={item.id} className="flex justify-between items-center p-4 bg-white border rounded-xl">
                  <div>
                    <div className="font-bold">{item.name}</div>
                    <div className="flex gap-2 text-xs">
                      <span className="bg-slate-100 px-2 rounded">{item.status}</span>
                      <span className="text-slate-500">{item.votes} votes</span>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteItem(item.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  );
};